import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';
import TemplateChangeDecisionDialog from './TemplateChangeDecisionDialog';

const selectionErrorKeys = {
  tracking_selection_needs_review: 'trackingReview.selection',
  invalid_tracking_response: 'trackingReview.response',
  invalid_tracking_settings: 'trackingReview.settings',
  invalid_tracking_branch: 'trackingReview.branch',
  tracking_context_limit: 'trackingReview.limit',
  tracking_already_configured: 'trackingReview.configured',
  no_tracking_actions: 'trackingReview.empty',
};

export default function FunnelStepsDialog({ project, stages, onClose, onSaved, onAdd, autoSuggest = false, autoActivate = false }) {
  const { t, i18n } = useTranslation();
  const [initialDraft] = useState(() => stages.map(({ actionId, label }) => ({ actionId, label })));
  const [draft, setDraft] = useState(() => initialDraft.map(stage => ({ ...stage })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [templateDecision, setTemplateDecision] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestionError, setSuggestionError] = useState(null);
  const [suggested, setSuggested] = useState(false);
  const started = useRef(false);
  const busy = saving || suggesting;
  const dirty = JSON.stringify(draft) !== JSON.stringify(initialDraft);
  const suggest = async () => {
    setSuggesting(true);
    setSuggestionError(null);
    try {
      const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(project.id)}/funnel/suggest`, {
        method: 'POST', body: JSON.stringify({ language: i18n?.resolvedLanguage || i18n?.language || 'en' }),
      });
      if (response?.status !== 'success' || !Array.isArray(response.stages) || !response.stages.length) {
        setSuggestionError(response?.status === 'needs_review'
          ? selectionErrorKeys[response.code] || 'trackingReview.selection' : 'suggestError');
        return;
      }
      const nextStages = response.stages.map(({ actionId, label }) => ({ actionId, label }));
      setDraft(nextStages);
      setSuggested(true);
      if (autoActivate) await save(undefined, false, nextStages);
    } catch (error) {
      setSuggestionError(error?.status === 422
        ? selectionErrorKeys[error.code] || 'trackingReview.selection' : 'suggestError');
    } finally {
      setSuggesting(false);
    }
  };
  useEffect(() => {
    if (autoSuggest && !initialDraft.length && !started.current) {
      started.current = true;
      suggest();
    }
    // Start only from the user's entry action, never a background update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSuggest]);
  const move = (index, direction) => setDraft(current => {
    const next = [...current];
    [next[index], next[index + direction]] = [next[index + direction], next[index]];
    return next;
  });
  const save = async (templateChangeMode, openWorkflow = false, stagesToSave = draft) => {
    setSaving(true);
    setError(false);
    try {
      const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(project.id)}/funnel`, {
        method: 'PATCH', body: JSON.stringify({ stages: stagesToSave, expectedStages: initialDraft,
          ...(templateChangeMode ? { template_change_mode: templateChangeMode } : {}) }),
      });
      if (response?.status === 'decision_required') {
        setTemplateDecision({ ...response, openWorkflow });
        return;
      }
      if (response?.status !== 'success') throw new Error('Tracking update failed');
      setTemplateDecision(null);
      onSaved(stagesToSave);
      if (openWorkflow) onAdd();
    } catch (_error) {
      setError(true);
    } finally {
      setSaving(false);
    }
  };
  return <><Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="funnel-editor-title">
    <DialogTitle id="funnel-editor-title">{t(autoActivate ? 'actionFunnel.activateTracking' : 'actionFunnel.editSteps')} · {project.title}</DialogTitle>
    <DialogContent>
      {autoActivate ? <Stack spacing={2}>
        {busy && <Stack direction="row" spacing={1.5} alignItems="center" role="status" aria-live="polite">
          <CircularProgress size={22} />
          <Typography variant="body2">{t(saving ? 'actionFunnel.activatingTracking' : 'actionFunnel.choosingSteps')}</Typography>
        </Stack>}
        {error && <Alert severity="error">{t('actionFunnel.activationError')}</Alert>}
        {suggestionError && <Alert severity="error">{t(`actionFunnel.${suggestionError}`)}</Alert>}
      </Stack> : <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('actionFunnel.editHelp')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{t('actionFunnel.saveError')}</Alert>}
      {suggestionError && <Alert severity="error" sx={{ mb: 2 }}>{t(`actionFunnel.${suggestionError}`)}</Alert>}
      {suggested && <Alert severity="info" sx={{ mb: 2 }}>{t('actionFunnel.suggestionPreview')}</Alert>}
      {!initialDraft.length && !draft.length && <Box sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="body2" sx={{ mb: 1.5 }}>{t('actionFunnel.suggestHelp')}</Typography>
        <Button variant="contained" disabled={busy} onClick={suggest}
          startIcon={suggesting ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeRoundedIcon />}>
          {t(suggesting ? 'actionFunnel.suggesting' : 'actionFunnel.suggest')}
        </Button>
        {suggesting && <Typography role="status" aria-live="polite" variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('actionFunnel.suggestingHelp')}</Typography>}
      </Box>}
      <Stack gap={2}>
        {draft.map((stage, index) => <Box key={stage.actionId} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField fullWidth size="small" label={t('actionFunnel.numberedStep', { number: index + 1 })}
            value={stage.label} disabled={busy} inputProps={{ maxLength: 120 }} error={!stage.label.trim()}
            onChange={event => setDraft(current => current.map(item => item.actionId === stage.actionId ? { ...item, label: event.target.value } : item))} />
          <IconButton disabled={busy || index === 0} aria-label={t('actionFunnel.moveUp', { name: stage.label })} onClick={() => move(index, -1)}><ArrowUpwardRoundedIcon /></IconButton>
          <IconButton disabled={busy || index === draft.length - 1} aria-label={t('actionFunnel.moveDown', { name: stage.label })} onClick={() => move(index, 1)}><ArrowDownwardRoundedIcon /></IconButton>
          <IconButton disabled={busy} aria-label={t('actionFunnel.remove', { name: stage.label })} onClick={() => setDraft(current => current.filter(item => item.actionId !== stage.actionId))}><CloseRoundedIcon /></IconButton>
        </Box>)}
      </Stack>
      {!draft.length && <Alert severity="info">{t('actionFunnel.noSteps')}</Alert>}
      {onAdd && <Button sx={{ mt: 2 }} disabled={busy || draft.some(stage => !stage.label.trim())} onClick={() => dirty ? save(undefined, true) : onAdd()}>{t(dirty ? 'actionFunnel.saveAndAdd' : 'actionFunnel.addStep')}</Button>}
      </>}
    </DialogContent>
    <DialogActions>
      <Button disabled={busy} onClick={onClose}>{t('workspaceHome.close')}</Button>
      {autoActivate ? (error || suggestionError) && <Button variant="contained" disabled={busy}
        onClick={() => error && draft.length ? save() : suggest()}>{t('actionFunnel.retryActivation')}</Button>
        : <Button variant="contained" disabled={busy || !dirty || draft.some(stage => !stage.label.trim())} onClick={() => save()}>{t(saving ? 'actionFunnel.saving' : 'actionFunnel.save')}</Button>}
    </DialogActions>
  </Dialog>
    <TemplateChangeDecisionDialog open={Boolean(templateDecision)} decision={templateDecision} t={t}
      onCancel={() => { setTemplateDecision(null); if (autoActivate) onClose(); }}
      onDecision={mode => save(mode, templateDecision?.openWorkflow)} />
  </>;
}
